import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { setAuthModal, setMessageContent } from '../../features/common/commonSlice';
import { setIsCurrentRealmSuspended } from '../../features/realmManagement/realmSlice';
import { hasAccessTokenRefresher, refreshAccessToken } from './tokenRefresher';
import { getCorrelationId } from '../../services/correlation/correlationId';
import {
    getSharedLogger,
    sanitizeErrorMessage,
    type ApiEvent,
    type ApiResult,
    type AutoPopulatedEventFields,
    type ErrorEvent as LogErrorEvent,
    type HttpMethod,
} from '../../services/logger';

// Create our baseQuery instance
const baseQuery = (apiUrl: string, args: any): any =>
    fetchBaseQuery({
        baseUrl: apiUrl,
        // credentials: 'include',
        prepareHeaders: (headers: any, { getState }) => {
            const token = (getState() as RootState)?.auth?.token;
            const realm = (getState() as RootState)?.common?.xRealm;
            const selectedOrg = (getState() as RootState)?.org?.selectedOrg;
            const urlSuffix = (getState() as RootState)?.apiSlice?.urlSuffix;
            const headerRealm = headers.get('x-realm');

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            if (realm && !headerRealm && !args?.excludeRealmHeader) {
                headers.set('x-realm', urlSuffix ? realm.replace(`${urlSuffix}_`, '') : realm);
            }
            if (headerRealm) {
                headers.set('x-realm', urlSuffix ? headerRealm.replace(`${urlSuffix}_`, '') : headerRealm);
            }

            if (selectedOrg?.id?.length && !headers.get('organizationId') && !args?.excludeOrgId) {
                headers.set('organizationId', selectedOrg?.id);
            }

            // Attach the per-session correlation ID (generated once after
            // login, cleared on logout — see `correlation/correlationId.ts`)
            // so Dynatrace can correlate FE session logs with BE traces.
            // Backends that don't consume the header simply ignore it, so
            // this is safe to send on every request. Skipped when empty
            // (pre-login) or when the caller has already set it explicitly.
            const currentCorrelationId = getCorrelationId();
            if (currentCorrelationId && !headers.get('X-Correlation-Id')) {
                headers.set('X-Correlation-Id', currentCorrelationId);
            }

            return headers;
        },
    });

const dispatchErrorToast = (api: any, message: any): void => {
    api.dispatch(
        setMessageContent({
            isOpen: true,
            message,
            duration: 3000,
        })
    );
};

const getErrorDataObject = (result: { error?: { data?: unknown; error?: string } }): Record<string, unknown> => {
    const errorData = result?.error?.data;
    return errorData && typeof errorData === 'object' ? (errorData as Record<string, unknown>) : {};
};

const getErrorToastMessage = (result: { error?: { data?: unknown; error?: string } }): string | undefined => {
    const errorData = getErrorDataObject(result);
    const message = (errorData.errorMessage ?? errorData.message ?? result?.error?.error) as string | undefined;
    return message;
};

// Removed a `/number/i` message-text guard here: it swallowed toasts for any error containing "number". Use per-endpoint `extraOptions: 'no-toast'` / `args.suppressErrorToast` instead.
const handleClientError = (result: any, args: any, extraOptions: any, api: any): void => {
    const requestUrl = typeof args === 'string' ? args : args?.url ?? '';
    if (!requestUrl.includes('signup') && !extraOptions && !args?.suppressErrorToast) {
        dispatchErrorToast(api, getErrorToastMessage(result));
    }
};

// On 401, attempt a single token refresh + retry before surfacing the
// re-authentication modal. This covers the common case where the Okta
// access token has just expired but the refresh token is still valid
// (e.g. user resumed the app after the token's ~3h lifetime).
const tryRefreshAndRetry = async (args: any, api: any, extraOptions: any, getState: any): Promise<any> => {
    const newToken = await refreshAccessToken();
    if (!newToken) {
        return null;
    }
    const retryExtraOptions = { ...extraOptions, isTokenRefreshRetry: true };
    const retryResult: any = await baseQuery(getState()?.apiSlice?.apiUrl, retryExtraOptions)(
        args,
        api,
        retryExtraOptions
    );
    return retryResult?.error?.status !== 401 ? retryResult : null;
};

const handleInterceptorError = (result: any, args: any, extraOptions: any, api: any): void => {
    const status = result?.error?.status;
    if (status === 401) {
        api.dispatch(setAuthModal(true));
        return;
    }
    if (status === 500 || status === 403 || status === 400 || status === 409) {
        handleClientError(result, args, extraOptions, api);
        return;
    }
    if (status === 423) {
        const rawUrl = typeof args === 'string' ? args : args?.url ?? '';
        const suspendedUrl = rawUrl.replace(/^\//, '');
        if (suspendedUrl.startsWith('rm/')) {
            api.dispatch(setIsCurrentRealmSuspended(true));
        }
        return;
    }
    if (status === 429) {
        dispatchErrorToast(api, getErrorToastMessage(result));
        return;
    }
    if (status === 'FETCH_ERROR') {
        dispatchErrorToast(api, "Can't connect to network from app.");
    }
};

// -----------------------------------------------------------------------
// API observability helpers
//
// Emit two spec events per RTK Query call — an `api_call` (INFO) capturing
// the full lifecycle (endpoint, method, statusCode, durationMs, result,
// retryCount) and, on non-success outcomes, an `api_error` (ERROR) alongside
// the existing toast dispatch. Covers the Confluence MUSTs "API call
// lifecycle (request sent, success, failure)", "API response times" and
// "Network errors" without altering any user-facing behavior.
//
// Everything below is observation-only: the API result is returned to RTK
// Query BEFORE the emission runs, and the emission is wrapped in a
// try/catch so a bad log payload can never break a request/response.
// -----------------------------------------------------------------------

const ALLOWED_HTTP_METHODS: ReadonlySet<HttpMethod> = new Set<HttpMethod>(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']);

/**
 * Structural subset of RTK Query's `QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>`
 * covering only the fields these helpers read. Written as a local type
 * rather than importing the RTK internal so this file stays decoupled
 * from RTK internal type paths (which have shifted between minor versions).
 */
type BaseQueryResult = {
    error?: { status?: number | string; data?: unknown; error?: string };
    data?: unknown;
    meta?: { response?: { status?: number } };
};

/**
 * Minimal shape of RTK Query's `BaseQueryApi` — only the field we log.
 * Kept structural so a future RTK type refactor doesn't force a change here.
 */
type ApiInstanceInfo = { endpoint?: string };

/**
 * Relative endpoint path with the query string stripped and any inline
 * email address redacted. The Confluence PII rule forbids logging emails,
 * names, phone numbers, addresses or free-text inputs — query strings can
 * carry any of those, so they are dropped wholesale. Path segments that
 * happen to contain an email (e.g. `/users/foo@bar.com/permissions`) are
 * masked to `/users/[redacted]/permissions` so the operation stays
 * groupable without leaking the address. UUIDs / opaque IDs are kept
 * on purpose — they are pseudonymous per the spec and are what makes
 * BE↔FE correlation useful.
 */
const EMAIL_PATTERN = /[^/\s?#]+@[^/\s?#]+\.[^/\s?#]+/g;

const extractEndpointPath = (args: string | FetchArgs): string => {
    const rawUrl = typeof args === 'string' ? args : args?.url ?? '';
    const withoutQuery = rawUrl.split('?')[0];
    if (!withoutQuery) {
        return '/';
    }
    const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
    return withLeadingSlash.replace(EMAIL_PATTERN, '[redacted]');
};

/**
 * HTTP method narrowed to one of the {@link HttpMethod} spec values,
 * defaulting to `GET`. Exotic methods (HEAD, OPTIONS) collapse to `GET`
 * so dashboards keep a fixed cardinality.
 */
const extractHttpMethod = (args: string | FetchArgs): HttpMethod => {
    if (typeof args === 'string') {
        return 'GET';
    }
    const method = (args?.method ?? 'GET').toUpperCase();
    return ALLOWED_HTTP_METHODS.has(method as HttpMethod) ? (method as HttpMethod) : 'GET';
};

/**
 * Resolve the HTTP status code as a number. RTK Query encodes non-HTTP
 * failures (FETCH_ERROR, TIMEOUT_ERROR, ABORTED, PARSING_ERROR, etc.) as
 * string statuses, which we collapse to `0` per the ApiEvent contract
 * ("0 for network/transport failures") so downstream consumers can rely
 * on `statusCode` being numeric.
 */
const extractStatusCode = (result: BaseQueryResult): number => {
    const errorStatus = result?.error?.status;
    if (typeof errorStatus === 'number') {
        return errorStatus;
    }
    const responseStatus = result?.meta?.response?.status;
    if (typeof responseStatus === 'number') {
        return responseStatus;
    }
    return 0;
};

/** Map an RTK Query result to the spec's `ApiResult` enum. */
const resolveApiResult = (result: BaseQueryResult): ApiResult => {
    if (!result?.error) {
        return 'success';
    }
    const status = result.error.status;
    if (status === 'TIMEOUT_ERROR') {
        return 'timeout';
    }
    if (status === 'ABORTED') {
        return 'canceled';
    }
    return 'failure';
};

/**
 * Emit one `api_call` INFO event per RTK Query call, plus an `api_error`
 * ERROR event when the outcome is not `success`. Silently no-ops when the
 * app hasn't bootstrapped the shared logger yet — keeps mobile safe until
 * its logger is wired.
 */
const emitApiObservabilityEvents = (opts: {
    apiInstance: ApiInstanceInfo;
    args: string | FetchArgs;
    result: BaseQueryResult;
    durationMs: number;
    retryCount: number;
}): void => {
    const l = getSharedLogger();
    if (!l) {
        return;
    }
    const { apiInstance, args, result, durationMs, retryCount } = opts;
    const apiName =
        typeof apiInstance?.endpoint === 'string' && apiInstance.endpoint.length > 0 ? apiInstance.endpoint : 'unknown';
    const httpMethod = extractHttpMethod(args);
    const endpoint = extractEndpointPath(args);
    const statusCode = extractStatusCode(result);
    const apiResult = resolveApiResult(result);

    // Typed explicitly (not as the wider `LogEventInput` union) so
    // TypeScript excess-property checks the shape against `ApiEvent`
    // rather than picking the first member of the discriminated union.
    const apiCallEvent: Omit<ApiEvent, AutoPopulatedEventFields> = {
        category: 'api',
        eventType: 'api_call',
        apiName,
        httpMethod,
        endpoint,
        statusCode,
        durationMs,
        result: apiResult,
        ...(retryCount > 0 ? { retryCount } : {}),
    };
    l.info(apiCallEvent);

    if (apiResult === 'success') {
        return;
    }

    const errorData = getErrorDataObject(result);
    const rawMessage =
        (errorData.errorMessage as string | undefined) ??
        (errorData.message as string | undefined) ??
        result?.error?.error ??
        'API call failed';
    // `globalThis` is cross-platform (web + RN): browsers expose
    // `location.pathname` there, React Native does not. The cast keeps
    // TypeScript happy under the mobile lib config, which types `window`
    // as `typeof window` (no `location`).
    const platformLocation = (globalThis as { location?: { pathname?: string } }).location;
    const apiErrorEvent: Omit<LogErrorEvent, AutoPopulatedEventFields> = {
        category: 'error',
        eventType: 'api_error',
        errorType: 'API_FAILURE',
        errorMessage: sanitizeErrorMessage(rawMessage),
        errorCode: statusCode > 0 ? String(statusCode) : undefined,
        contextPageName: platformLocation?.pathname ?? 'unknown',
        contextAction: `${httpMethod} ${endpoint}`,
    };
    l.error(apiErrorEvent);
};

const baseQueryWithInterceptor: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args: any,
    api: any,
    extraOptions: any
) => {
    const { getState } = api;
    const apiSlice = getState()?.apiSlice;
    // `performance.now()` is monotonic (unaffected by wall-clock adjustments)
    // and available on every runtime the app targets. Guard on `now` being
    // a function (not just `performance` being defined) because a few older
    // RN polyfills expose the object without the method — and reading a
    // missing method must not throw on the API hot path.
    const hasPerfNow = typeof performance !== 'undefined' && typeof performance.now === 'function';
    const start = hasPerfNow ? performance.now() : Date.now();
    let retryCount = 0;
    let result: any = await baseQuery(apiSlice?.apiUrl, extraOptions)(args, api, extraOptions);

    const is401Error = result?.error?.status === 401;
    const canRefresh = hasAccessTokenRefresher();
    const isRetry = extraOptions?.isTokenRefreshRetry;

    if (is401Error && canRefresh && !isRetry) {
        const retryResult = await tryRefreshAndRetry(args, api, extraOptions, getState);
        if (retryResult) {
            result = retryResult;
            retryCount = 1;
        }
    }

    const end = hasPerfNow ? performance.now() : Date.now();
    const durationMs = Math.round(end - start);

    try {
        handleInterceptorError(result, args, extraOptions, api);
    } catch {
        // Never allow interceptor-side toast/formatting logic to break RTK Query endpoint processing.
    }

    try {
        emitApiObservabilityEvents({ apiInstance: api, args, result, durationMs, retryCount });
    } catch {
        // Observability is purely additive — a logger crash must not break the API flow.
    }

    return result;
};
//
/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded
 */
export const api = createApi({
    reducerPath: 'api',
    tagTypes: [
        'Realms',
        'RealmById',
        'Orgs',
        'Dashboards',
        'device-settings',
        'Schedules',
        'Profile',
        'ActiveDashboards',
        'Users',
        'Notifications',
        'DashboardById',
        'GroupsHierarchy',
        'DeviceList',
        'Timeline',
        'GroupById',
        'AvailableCommands',
        'WidgetById',
        'UserById',
        'DeviceTags',
        'RealmChildren',
        'RealmSettings',
        'Roles',
        'Widgets',
        'RoleById',
        'DEVICEMODELS',
        'MultiLoads',
        'UsersByRoleId',
        'CustomNotifications',
        'Notifications',
        'Automations',
        'PlanDetails',
        'allPlans',
        'subscriptionDetails',
        'allSubscriptions',
        'Threshold',
        'AllLoads',
        'LoadDetails',
        'Properties',
        'TimelineById',
        'RealmTimelineTags',
        'SnoozeByDeviceId',
        'Virtual_Loads',
        'DeviceById',
        'UpcomingById',
        'UpcomingList',
        'VirtualLoadDetails',
        'TimelineComment',
        'orgSubscription',
        'orgDetails',
        'PaginatedRealms',
        'PaginatedOrgs',
        'ServiceContact',
        'UserEula',
        'CommentBySerialNumber',
        'userRoles',
        'ChannelByDeviceId',
        'ChannelLibrary',
        'OpenRealmSettings',
        'EmergencyContacts',
        'GET-TABLE',
        'ChannelIcons',
        'Icons',
        'PreferredLanguage',
        'FeatureFlag',
        'allSubscriptionsV2',
        'subscriptionV2Details',
        'DeviceLicenses',
        'LoadGroups',
        'LoadGroupsById',
        'LoadBalancingAutomations',
        'getLoadGroupsById',
        'DeleteLoadGroup',
        'ExportStatus',
        'UserAgreements',
        'UserAgreementsPublic',
    ],
    /**
     * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
     */
    baseQuery: baseQueryWithInterceptor,
    /**
     * This api has endpoints injected in adjacent files,
     * which is why no endpoints are shown below.
     * If you want all endpoints defined in the same file, they could be included here instead
     */
    endpoints: () => ({}),
});
